import React, { createContext, useState, useEffect, ReactNode } from "react";
import api from "../../api/api"; // Axios instance for API calls

interface WorkType {
  id: number;
  workTypeName: string;
  itemCode?: string;
}

interface WorkTypesContextType {
  workTypes: WorkType[];
  pagination: any;
  loading: boolean;
  fetchWorkTypes: (page?: number, perPage?: number) => Promise<void>;
  addWorkType: (workType: any) => Promise<void>;
  editWorkType: (workType: any) => Promise<void>;
  deleteWorkType: (id: number) => Promise<void>;
}

export const WorkTypesContext = createContext<WorkTypesContextType | undefined>(undefined);

interface WorkTypesProviderProps {
  children: ReactNode;
}

const WorkTypesProvider: React.FC<WorkTypesProviderProps> = ({ children }) => {
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);

  const [pagination, setPagination] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Fetch all work types with pagination
  const fetchWorkTypes = async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/work-types?${params.toString()}`);
      
      // Handle paginated response structure
      let workTypesData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          workTypesData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          workTypesData = responseData.data;
          paginationData = responseData.pagination;
        }
      } else if (Array.isArray(responseData?.data)) {
        workTypesData = responseData.data;
      } else if (Array.isArray(responseData)) {
        workTypesData = responseData;
      }
      
      setWorkTypes(workTypesData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch work types:", error);
      setWorkTypes([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  // Add a new work type
  const addWorkType = async (workType) => {
    try {
      const response = await api.post("/work-types", workType);
      setWorkTypes((prev) => [...prev, response.data?.data || response.data]);
    } catch (error) {
      console.error("Failed to add work type:", error);
      throw error;
    }
  };

  // Edit an existing work type
  const editWorkType = async (workType) => {
    try {
      const response = await api.put(`/work-types/${workType.id}`, workType);
      setWorkTypes((prev) =>
        prev.map((item) => (item.id === workType.id ? (response.data?.data || response.data) : item))
      );
    } catch (error) {
      console.error("Failed to edit work type:", error);
      throw error;
    }
  };

  // Delete a work type
  const deleteWorkType = async (id) => {
    try {
      await api.delete(`/work-types/${id}`);
      setWorkTypes((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete work type:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchWorkTypes(1, 15);
  }, []);

  return (
    <WorkTypesContext.Provider
      value={{
        workTypes,
        pagination,
        loading,
        fetchWorkTypes,
        addWorkType,
        editWorkType,
        deleteWorkType,
      }}
    >
      {children}
    </WorkTypesContext.Provider>
  );
};

export default WorkTypesProvider;
export { WorkTypesProvider };
